import { AdminPagination as ZhPagination } from "@/views/components/AdminPagination";
import { Avatar, Button, Card, Col, Input, Row, Select, Space, Statistic, Tag, Tooltip } from "antd";
import { PageAlert as Alert } from "@/views/components/PageAlert";
import dayjs from "dayjs";
import type { EChartsOption } from "echarts";
import { useEffect, useMemo, useRef, useState } from "react";
import { DateRangePicker as ZhDateRangePicker, type TableColumnProps } from "zhihao-ui-react";

import request from "@/api";
import { prodTypeService } from "@/api/services/prodTypeService";
import { DEFAULT_VALUE, LOG_DATE_FORMAT_END, LOG_DATE_FORMAT_START } from "@/config";
import Permission from "@/views/components/Permission";
import { TableSearchIcon } from "@/views/components/TableSearchIcon";
import { AdminDetailTable } from "@/views/components/DetailTable";
import authenticatedIcon from "@/views/assets/images/authenticated-icon.svg";
import userDefaultAvatar from "@/views/assets/images/user-default-avatar.png";
import { areaLib } from "@/utils/areaLib";
import { formatTimeEn } from "@/utils/formatter";
import { formatNumber } from "@/utils/number";
import createChartHelpers from "@/utils/useChart";
import { formatNumberWithUnitAndChinese, formartTimeWithUnitAndChinese } from "@zhihaoscm/utils";
import {
    CONSIGN_PUBLISH_STATE,
    CONSIGN_STATE,
    ENTERPRISE_SUPPLIER_TYPE,
    CLIENT_TYPE_USER,
    MODULE_TYPE,
    TRACK_TYPE_USER
} from "@zhihaoscm/meta";
import ProfileChart from "./infoDetail/components/userProfile/components/chart";

export interface CustomerProfileBasic {
    userName?: string;
    company?: string;
    identityTags?: string[];
    age?: number | string;
    gender?: number | string;
    cityCode?: string;
    commonProvince?: string;
    commonCity?: string;
    avatarFile?: { path?: string };
}

export interface CustomerProfileOverview {
    orderTradeAmount?: number | string;
    settlementAmount?: number | string;
    refundAmount?: number | string;
    orderTon?: number | string;
    actualSettlementTon?: number | string;
    totalOrders?: number | string;
    completedOrders?: number | string;
}

export interface CustomerProfileStats {
    footprintCount?: number;
    collectCount?: number;
    followSupplierCount?: number;
}

export interface CustomerProfileFrequencyItem {
    dateStr?: string;
    orderTradeAmount?: number;
    settlementAmount?: number;
    refundAmount?: number;
    orderTon?: number;
    actualSettlementTon?: number;
    totalOrders?: number;
}

export interface CustomerProfileFrequency {
    frequencyList?: CustomerProfileFrequencyItem[];
}

export interface CustomerProfilePurchaseCategory {
    productTypeName?: string;
    orderCount?: number;
    orderTon?: number;
}

export interface CustomerProfileBehavior {
    moduleName?: string;
    stayTime?: number;
}

export interface CustomerProfileInterestSand {
    productTypeName?: string;
    browseCount?: number;
    stayTime?: number;
}

export interface CustomerProfileGoodsItem {
    __index?: number;
    productId?: string;
    productType?: number;
    mainFile?: { path?: string };
    title?: string;
    category?: string;
    supplierName?: string;
    goodsCommissioner?: string;
    goodsState?: number | string;
    publishState?: number | string;
    browseTime?: string;
    collectTime?: string;
}

export interface CustomerProfileSupplierItem {
    __index?: number;
    supplierId?: number;
    supplierName?: string;
    supplierType?: number | string;
    groupName?: string;
    legalPerson?: string;
    address?: string;
    followTime?: string;
}

export interface CustomerProfilePageResult<T> {
    list?: T[];
    total?: number;
}

export interface CustomerProfileBehaviorLog {
    time?: string;
    clientType?: string;
    module?: string;
    eventName?: string;
    stayDuration?: number;
    type?: string | number;
    detailContent?: string | Record<string, unknown>;
}

export interface CustomerProfileBehaviorTotals {
    totalStayDurationMs?: number;
    totalVisitCount?: number;
}

export interface CustomerProfileTimeRange {
    beginTime: string;
    endTime: string;
}

export interface CustomerProfileProductType {
    id: number | string;
    name: string;
    areaAbbreviation?: string;
}

interface CustomerProfileBehaviorFilters {
    keyword?: string;
    clientType?: string;
    functionType?: string;
    type?: string;
    beginTime?: string;
    endTime?: string;
}

interface CustomerProfileAttentionFilters {
    keyword?: string;
    productTypeId?: string;
    supplierName?: string;
    supplierType?: Array<string | number>;
}

type CustomerProfileBehaviorQuery = { customerId: number } & CustomerProfileBehaviorFilters;

export interface CustomerProfilePageProps {
    customerId: number;
    onBack?: () => void;
    loadTimeRange?: (params: { customerId: number }) => Promise<CustomerProfileTimeRange>;
    loadBasic?: (params: { customerId: number }) => Promise<CustomerProfileBasic>;
    loadOverview?: (params: {
        customerId: number;
        beginTime: string;
        endTime: string;
    }) => Promise<CustomerProfileOverview>;
    loadStats?: (params: { customerId: number; beginTime: string; endTime: string }) => Promise<CustomerProfileStats>;
    loadFrequency?: (params: {
        customerId: number;
        beginTime: string;
        endTime: string;
        timeGranularity: number;
    }) => Promise<CustomerProfileFrequency>;
    loadPurchaseCategory?: (params: {
        customerId: number;
        beginTime: string;
        endTime: string;
        sortType: number;
    }) => Promise<CustomerProfilePurchaseCategory[]>;
    loadBehavior?: (params: {
        customerId: number;
        beginTime: string;
        endTime: string;
    }) => Promise<CustomerProfileBehavior[]>;
    loadInterestSandTop?: (params: {
        customerId: number;
        beginTime: string;
        endTime: string;
        sortType: number;
    }) => Promise<CustomerProfileInterestSand[]>;
    loadFootprint?: (params: {
        customerId: number;
        page: number;
        size: number;
        beginTime: string;
        endTime: string;
        keyword?: string;
        productTypeId?: string;
        supplierName?: string;
    }) => Promise<CustomerProfilePageResult<CustomerProfileGoodsItem>>;
    loadCollectedGoods?: (params: {
        customerId: number;
        page: number;
        size: number;
        beginTime: string;
        endTime: string;
        keyword?: string;
        productTypeId?: string;
        supplierName?: string;
    }) => Promise<CustomerProfilePageResult<CustomerProfileGoodsItem>>;
    loadFollowedSupplier?: (params: {
        customerId: number;
        page: number;
        size: number;
        beginTime: string;
        endTime: string;
        supplierName?: string;
        supplierType?: Array<string | number>;
    }) => Promise<CustomerProfilePageResult<CustomerProfileSupplierItem>>;
    loadProductTypes?: (params: { keyword?: string; productType: number }) => Promise<CustomerProfileProductType[]>;
    loadBehaviorLogs?: (params: {
        customerId: number;
        page: number;
        size: number;
        keyword?: string;
        clientType?: string;
        functionType?: string;
        type?: string;
        beginTime?: string;
        endTime?: string;
    }) => Promise<CustomerProfilePageResult<CustomerProfileBehaviorLog>>;
    loadBehaviorTotals?: (params: CustomerProfileBehaviorQuery) => Promise<CustomerProfileBehaviorTotals>;
    loadBehaviorExport?: (params: {
        customerId: number;
        page?: number;
        size?: number;
        keyword?: string;
        clientType?: string;
        functionType?: string;
        type?: string;
        beginTime?: string;
        endTime?: string;
    }) => Promise<unknown>;
}

/** 旧 Vue 页面首次进入画像时默认查询最近一个月（含今天）。 */
const getDefaultProfileTimeRange = (): CustomerProfileTimeRange => ({
    beginTime: dayjs().subtract(1, "month").add(1, "day").startOf("day").format("YYYY-MM-DDTHH:mm:ss"),
    endTime: dayjs().endOf("day").format("YYYY-MM-DDTHH:mm:ss")
});

const defaultProfileTimeRange = getDefaultProfileTimeRange();
const genderLabels: Record<number, string> = { 0: "男", 1: "女" };
const { getLegendIcon, getTooltipIcon } = createChartHelpers();

const profileChartSeries = [
    ["订单交易金额", "orderTradeAmount", "#1777FF", "元"],
    ["结算金额", "settlementAmount", "#7FD1EF", "元"],
    ["退款金额", "refundAmount", "#EB6C69", "元"],
    ["下单吨数", "orderTon", "#F2A750", "吨"],
    ["实际结算吨数", "actualSettlementTon", "#86D59A", "吨"],
    ["生成订单", "totalOrders", "#FFA6E4", "笔"]
] as const;

/** 将旧 Vue 交易频率数据转换为同样的多系列柱状图。 */
const buildFrequencyChartOption = (items: CustomerProfileFrequencyItem[]): EChartsOption => {
    // 空结果仍保留图表容器，但不创建无数据的坐标轴，避免 ECharts 在空数据下触发内部异常。
    if (!items.length) return {};
    return {
        grid: { left: 20, right: 20, bottom: "28%", top: 20, containLabel: true },
        legend: {
            show: true,
            bottom: "0",
            padding: [0, 0, 10, 0],
            itemWidth: 16,
            itemHeight: 16,
            textStyle: {
                fontSize: 14,
                lineHeight: 14,
                rich: { a: { verticalAlign: "middle" } },
                padding: [0, 0, -2, 0]
            },
            data: profileChartSeries.map(([name, , color]) => ({
                name,
                icon: getLegendIcon("bar", color.replace("#", ""), false)
            }))
        },
        tooltip: {
            trigger: "axis",
            // 图表卡片内部展示浮层，靠近边缘时不遮挡相邻卡片。
            confine: true,
            formatter: (params: unknown) => {
                const list = (Array.isArray(params) ? params : [params]) as Array<{
                    axisValue?: string;
                    seriesName?: string;
                    value?: number;
                }>;
                // Vue 图表只展示一次日期，每个系列显示色块、数值及单位。
                const date = String(list[0]?.axisValue || "").replace(
                    /[&<>"']/g,
                    value => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[value] || value
                );
                const rows = list.flatMap(entry => {
                    const config = profileChartSeries.find(([name]) => name === entry.seriesName);
                    if (!config) return [];
                    const [name, , color, unit] = config;
                    const numericValue = Number(entry.value || 0);
                    const value =
                        numericValue === 0
                            ? "0"
                            : name === "生成订单"
                              ? numericValue.toLocaleString("en-US")
                              : formatNumber(numericValue, 2);
                    return [
                        `<div class="profile-frequency-tooltip-item"><div class="profile-frequency-tooltip-icon">${getTooltipIcon("bar", color)}</div><div class="profile-frequency-tooltip-label">${name}</div><div class="profile-frequency-tooltip-value"><strong>${value}</strong><span>${unit}</span></div></div>`
                    ];
                });
                return `<div class="profile-frequency-tooltip"><div class="profile-frequency-tooltip-title">${date}</div>${rows.join("")}</div>`;
            }
        },
        xAxis: {
            type: "category",
            data: items.map(item => item.dateStr || ""),
            axisLabel: { interval: 0, color: "#000000b3", hideOverlap: true },
            axisTick: { show: false },
            axisLine: { lineStyle: { color: "#F0F0F0" } }
        },
        yAxis: {
            type: "value",
            axisLabel: { color: "#000000b3" },
            axisLine: { show: true, lineStyle: { color: "#F0F0F0" } },
            splitLine: { lineStyle: { type: "dashed", color: "#D9D9D9" } }
        },
        dataZoom: [
            {
                brushSelect: false,
                show: true,
                start: 0,
                end: items.length > 20 ? (20 / items.length) * 100 : 100,
                bottom: "10%",
                borderColor: "#ECF2FF",
                backgroundColor: "#fff",
                selectedDataBackground: {
                    lineStyle: { color: "#739AE0" },
                    areaStyle: { color: "#E3EFFF" }
                },
                dataBackground: {
                    lineStyle: { color: "#C0D7FF" },
                    areaStyle: { color: "#EAF3FF" }
                },
                handleStyle: { borderColor: "#85B1FF" },
                moveHandleStyle: { color: "#85B1FF" }
            }
        ],
        series: profileChartSeries.map(([name, key, color]) => ({
            name,
            type: "bar" as const,
            barMaxWidth: 20,
            itemStyle: { color, borderRadius: 2 },
            data: items.map(item => item[key as keyof CustomerProfileFrequencyItem] ?? 0)
        }))
    };
};

/** 将旧 Vue 行为分布数据转换为雷达图，并保留右侧数值图例。 */
const buildBehaviorChartOption = (items: CustomerProfileBehavior[]): EChartsOption => {
    // 旧接口在没有行为记录时返回空数组；ECharts 雷达图不能接受空 indicator。
    if (!items.length) return {};
    const values = items.map(item => Number(item.stayTime) || 0);
    const max = Math.max(...values, 1);
    return {
        radar: {
            indicator: items.map(item => ({ name: item.moduleName || DEFAULT_VALUE, max })),
            axisLine: { lineStyle: { color: "#F0F0F0" } },
            axisName: { color: "#333333", fontSize: 12 },
            splitArea: { areaStyle: { color: ["transparent", "transparent"] } },
            radius: 120,
            center: ["50%", "55%"]
        },
        series: [{ type: "radar", data: [{ symbol: "none", lineStyle: { color: "#1777FF" }, value: values }] }]
    };
};

/** 复用 Vue 用户画像基础信息接口。 */
export async function loadCustomerProfileBasic(params: { customerId: number }) {
    const response = await request.service.get("/admin/user-profile/basic", { params });
    return response.data as CustomerProfileBasic;
}

/** 复用 Vue 用户画像全部时间范围接口。 */
export async function loadCustomerProfileTimeRange(params: { customerId: number }) {
    const response = await request.service.get("/admin/user-profile/time-range", { params });
    return response.data as CustomerProfileTimeRange;
}

/** 复用 Vue 用户画像交易概览接口。 */
export async function loadCustomerProfileOverview(params: { customerId: number; beginTime: string; endTime: string }) {
    const response = await request.service.get("/admin/user-profile/trade-overview", { params });
    return response.data as CustomerProfileOverview;
}

/** 复用 Vue 用户画像关注统计接口。 */
export async function loadCustomerProfileStats(params: { customerId: number; beginTime: string; endTime: string }) {
    const response = await request.service.get("/admin/user-profile/profile/stats", { params });
    return response.data as CustomerProfileStats;
}

/** 复用 Vue 用户画像交易频率接口，默认使用月粒度。 */
export async function loadCustomerProfileFrequency(params: {
    customerId: number;
    beginTime: string;
    endTime: string;
    timeGranularity: number;
}) {
    const response = await request.service.get("/admin/user-profile/trade-frequency", { params });
    return response.data as CustomerProfileFrequency;
}

/** 复用 Vue 用户画像购买品类排行接口。 */
export async function loadCustomerProfilePurchaseCategory(params: {
    customerId: number;
    beginTime: string;
    endTime: string;
    sortType: number;
}) {
    const response = await request.service.get("/admin/user-profile/purchase-category", { params });
    return response.data as CustomerProfilePurchaseCategory[];
}

/** 复用 Vue 用户画像行为分布接口。 */
export async function loadCustomerProfileBehavior(params: { customerId: number; beginTime: string; endTime: string }) {
    const response = await request.service.get("/admin/user-profile/behavior", { params });
    return response.data as CustomerProfileBehavior[];
}

/** 复用 Vue 用户画像感兴趣砂石排行接口。 */
export async function loadCustomerProfileInterestSandTop(params: {
    customerId: number;
    beginTime: string;
    endTime: string;
    sortType: number;
}) {
    const response = await request.service.get("/admin/user-profile/interest-sand-top", { params });
    return response.data as CustomerProfileInterestSand[];
}

/** 复用 Vue 商品足迹分页接口。 */
export async function loadCustomerProfileFootprint(params: {
    customerId: number;
    page: number;
    size: number;
    beginTime: string;
    endTime: string;
    keyword?: string;
    productTypeId?: string;
    supplierName?: string;
}) {
    const response = await request.service.get("/admin/user-profile/goods-footprint/paging", { params });
    return response.data as CustomerProfilePageResult<CustomerProfileGoodsItem>;
}

/** 复用 Vue 收藏商品分页接口。 */
export async function loadCustomerProfileCollectedGoods(params: {
    customerId: number;
    page: number;
    size: number;
    beginTime: string;
    endTime: string;
    keyword?: string;
    productTypeId?: string;
    supplierName?: string;
}) {
    const response = await request.service.get("/admin/user-profile/collected-goods/paging", { params });
    return response.data as CustomerProfilePageResult<CustomerProfileGoodsItem>;
}

/** 复用 Vue 关注供应商分页接口。 */
export async function loadCustomerProfileFollowedSupplier(params: {
    customerId: number;
    page: number;
    size: number;
    beginTime: string;
    endTime: string;
    supplierName?: string;
    supplierType?: Array<string | number>;
}) {
    const response = await request.service.get("/admin/user-profile/followed-supplier/paging", { params });
    return response.data as CustomerProfilePageResult<CustomerProfileSupplierItem>;
}

/** 复用旧 Vue SelectRemote 的寄售商品品类接口。 */
export async function loadCustomerProfileProductTypes(params: { keyword?: string; productType: number }) {
    return (await prodTypeService.selectorInProduct(params)) as CustomerProfileProductType[];
}

/** 复用 Vue 用户画像行为埋点分页接口。 */
export async function loadCustomerProfileBehaviorLogs(params: {
    customerId: number;
    page: number;
    size: number;
    keyword?: string;
    clientType?: string;
    functionType?: string;
    type?: string;
    beginTime?: string;
    endTime?: string;
}) {
    const response = await request.service.get("/admin/user-profile/paging", { params });
    return response.data as CustomerProfilePageResult<CustomerProfileBehaviorLog>;
}

/** 复用 Vue 用户画像行为埋点统计接口，并沿用列表的筛选参数。 */
export async function loadCustomerProfileBehaviorTotals(params: CustomerProfileBehaviorQuery) {
    const response = await request.service.get("/admin/user-profile/total", { params });
    return response.data as CustomerProfileBehaviorTotals;
}

/** 复用 Vue 用户画像行为埋点导出接口并触发浏览器下载。 */
export async function loadCustomerProfileBehaviorExport(params: {
    customerId: number;
    page?: number;
    size?: number;
    keyword?: string;
    clientType?: string;
    functionType?: string;
    type?: string;
    beginTime?: string;
    endTime?: string;
}) {
    const response = await request.service.get("/admin/user-profile/export-use-profile", {
        params,
        responseType: "blob"
    });
    // RequestHttp 对成功响应默认解包，Blob 会直接返回；兼容原始 Axios 响应便于独立调用。
    const blob = (response instanceof Blob ? response : response.data) as Blob;
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `用户画像${params.customerId}行为埋点.xlsx`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
    return blob;
}

const display = (value?: number | string) => (value == null || value === "" ? DEFAULT_VALUE : String(value));

/** 旧 Vue DataCard 使用“万/亿”缩写，避免大数直接撑破交易概览卡片。 */
const formatProfileMetric = (value?: number | string, unit?: string) => {
    const parsed = Number(value ?? 0);
    const normalized = Number.isFinite(parsed) ? parsed : 0;
    try {
        return formatNumberWithUnitAndChinese(normalized, unit);
    } catch {
        // 极端超大数仍保持紧凑显示，避免统计接口异常值把卡片撑开。
        const precise = normalized.toLocaleString("en-US", { maximumFractionDigits: 2 });
        const compact = new Intl.NumberFormat("en-US", { notation: "compact", maximumFractionDigits: 2 }).format(
            normalized
        );
        return { precise, imprecise: compact, baseUnit: "" as const, zh: precise };
    }
};

/** 统一沿用旧 Vue 列表的 YYYY-MM-DD HH:mm 展示格式。 */
const formatShortDate = (value?: string) => (value ? dayjs(value).format("YYYY-MM-DD HH:mm") : DEFAULT_VALUE);
const formatBehaviorDate = (value?: string) => (value ? dayjs(value).format("YYYY-MM-DD HH:mm:ss") : DEFAULT_VALUE);

/** 行为埋点接口要求 ISO 风格的日期边界；筛选框显示用的日期格式不能直接拼成带空格的时间。 */
export const normalizeCustomerProfileBehaviorFilters = (filters: CustomerProfileBehaviorFilters) =>
    Object.fromEntries(
        Object.entries(filters)
            .filter(([, value]) => value != null && value !== "")
            .map(([key, value]) => {
                if (key === "beginTime" && value) return [key, dayjs(value).format(LOG_DATE_FORMAT_START)];
                if (key === "endTime" && value) return [key, dayjs(value).format(LOG_DATE_FORMAT_END)];
                return [key, value];
            })
    ) as CustomerProfileBehaviorFilters;

/** 用户画像日期区间使用可展开的双月选择器，与旧 Vue 页面保持相同的筛选入口。 */
function ProfileDateRangePicker({
    value,
    onChange,
    timeGranularity
}: {
    value: [string, string];
    onChange: (_value: unknown, formattedValue: [string, string] | null) => void;
    timeGranularity: number;
}) {
    /** 与旧版画像日期控件一致：月粒度允许更早日期，其余粒度只允许最近一年。 */
    const disabledDate = (date: Date) => {
        const candidate = dayjs(date);
        const today = dayjs();
        return (
            candidate.isAfter(today, "day") ||
            (timeGranularity !== 3 && candidate.isBefore(today.subtract(365, "day"), "day"))
        );
    };

    return (
        <ZhDateRangePicker
            className="customer-profile-date-range-picker"
            data-testid="zhihao-date-range-picker"
            clearable={false}
            disabledDate={disabledDate}
            endPlaceholder="结束日期"
            format="YYYY-MM-DD"
            placement="bottom"
            // Vue 的双月面板以 200px 输入框中心展开；Ant 默认左对齐，需要补齐锚点偏移。
            popupAlign={{ offset: [-225, 3] }}
            rangeSeparator="-"
            startPlaceholder="开始日期"
            type="daterange"
            value={value}
            valueFormat="YYYY-MM-DD"
            onChange={onChange}
        />
    );
}

export function CustomerProfilePage({
    customerId,
    onBack,
    loadTimeRange = loadCustomerProfileTimeRange,
    loadBasic = loadCustomerProfileBasic,
    loadOverview = loadCustomerProfileOverview,
    loadStats = loadCustomerProfileStats,
    loadFrequency = loadCustomerProfileFrequency,
    loadPurchaseCategory = loadCustomerProfilePurchaseCategory,
    loadBehavior = loadCustomerProfileBehavior,
    loadInterestSandTop = loadCustomerProfileInterestSandTop,
    loadFootprint = loadCustomerProfileFootprint,
    loadCollectedGoods = loadCustomerProfileCollectedGoods,
    loadFollowedSupplier = loadCustomerProfileFollowedSupplier,
    loadProductTypes = loadCustomerProfileProductTypes,
    loadBehaviorLogs = loadCustomerProfileBehaviorLogs,
    loadBehaviorTotals = loadCustomerProfileBehaviorTotals,
    loadBehaviorExport = loadCustomerProfileBehaviorExport
}: CustomerProfilePageProps) {
    const [profileRange, setProfileRange] = useState<CustomerProfileTimeRange>(defaultProfileTimeRange);
    const [rangeDraft, setRangeDraft] = useState<CustomerProfileTimeRange>(defaultProfileTimeRange);
    const [timeGranularity, setTimeGranularity] = useState(1);
    const [purchaseSortType, setPurchaseSortType] = useState(2);
    const [interestSortType, setInterestSortType] = useState(2);
    const [basic, setBasic] = useState<CustomerProfileBasic>();
    const [overview, setOverview] = useState<CustomerProfileOverview>();
    const [stats, setStats] = useState<CustomerProfileStats>();
    const [frequency, setFrequency] = useState<CustomerProfileFrequencyItem[]>([]);
    const [purchaseCategory, setPurchaseCategory] = useState<CustomerProfilePurchaseCategory[]>([]);
    const [behavior, setBehavior] = useState<CustomerProfileBehavior[]>([]);
    const [interestSandTop, setInterestSandTop] = useState<CustomerProfileInterestSand[]>([]);
    const [attentionTab, setAttentionTab] = useState<"footprint" | "collected" | "supplier">("footprint");
    const [attentionPage, setAttentionPage] = useState(1);
    const [attentionTotal, setAttentionTotal] = useState(0);
    const [footprint, setFootprint] = useState<CustomerProfileGoodsItem[]>([]);
    const [collectedGoods, setCollectedGoods] = useState<CustomerProfileGoodsItem[]>([]);
    const [followedSupplier, setFollowedSupplier] = useState<CustomerProfileSupplierItem[]>([]);
    const [productTypeOptions, setProductTypeOptions] = useState<Array<{ label: string; value: string }>>([]);
    const [productTypeKeyword, setProductTypeKeyword] = useState("");
    const [productTypeLoading, setProductTypeLoading] = useState(false);
    const productTypeRequestRef = useRef(0);
    const [attentionDraftFilters, setAttentionDraftFilters] = useState<CustomerProfileAttentionFilters>({});
    const [attentionFilters, setAttentionFilters] = useState<CustomerProfileAttentionFilters>({});
    const [behaviorLogs, setBehaviorLogs] = useState<CustomerProfileBehaviorLog[]>([]);
    const [behaviorTotal, setBehaviorTotal] = useState(0);
    const [behaviorTotals, setBehaviorTotals] = useState<CustomerProfileBehaviorTotals>();
    const [behaviorPage, setBehaviorPage] = useState(1);
    const [behaviorDraftFilters, setBehaviorDraftFilters] = useState<CustomerProfileBehaviorFilters>({});
    const [behaviorFilters, setBehaviorFilters] = useState<CustomerProfileBehaviorFilters>({});
    const [behaviorExporting, setBehaviorExporting] = useState(false);
    const [errorMessage, setErrorMessage] = useState("");

    /** Vue 详情头在月、年粒度下展示当前日期范围的文字摘要，日粒度不展示。 */
    const profileDateRangeLabel = useMemo(() => {
        const begin = dayjs(profileRange.beginTime);
        const end = dayjs(profileRange.endTime);
        if (!begin.isValid() || !end.isValid()) return "";
        if (timeGranularity === 3) return `${begin.year()}年${begin.month() + 1}月~${end.year()}年${end.month() + 1}月`;
        if (timeGranularity === 4) return `${begin.year()}年~${end.year()}年`;
        return "";
    }, [profileRange.beginTime, profileRange.endTime, timeGranularity]);

    /** 加载 Vue 的全部画像时间范围，并同步当前筛选草稿。 */
    const applyAllProfileTimeRange = async () => {
        try {
            const nextRange = await loadTimeRange({ customerId });
            setProfileRange(nextRange);
            setRangeDraft(nextRange);
            setErrorMessage("");
        } catch {
            setErrorMessage("用户画像时间范围加载失败");
        }
    };

    /** 应用手工输入的画像起止时间。 */
    const applyProfileTimeRange = () => {
        setProfileRange({ ...rangeDraft });
    };

    /** 将日期选择器的可见日期补齐为后端需要的起止时刻。 */
    const normalizeProfileTime = (value: string, endOfDay: boolean) => {
        if (!value || value.includes("T") || value.includes(" ")) return value;
        const parsed = dayjs(value);
        if (!parsed.isValid()) return value;
        return (endOfDay ? parsed.endOf("day") : parsed.startOf("day")).format("YYYY-MM-DDTHH:mm:ss");
    };

    /** 沿用旧 Vue ZhDatePicker 的日期范围更新格式，保留后端需要的完整时间字符串。 */
    const updateProfileTimeRangeDraft = (_value: unknown, formattedValue: [string, string] | null) => {
        if (!formattedValue) return;
        const nextRange = {
            beginTime: normalizeProfileTime(formattedValue[0] || "", false),
            endTime: normalizeProfileTime(formattedValue[1] || "", true)
        };
        setRangeDraft(nextRange);
        // 旧 Vue 日期选择器变更后立即触发画像子模块查询，详情页隐藏“应用”按钮时也保持该行为。
        setProfileRange(nextRange);
    };

    useEffect(() => {
        let disposed = false;
        const rangeParams = {
            customerId,
            beginTime: profileRange.beginTime,
            endTime: profileRange.endTime
        };
        void Promise.all([
            loadBasic({ customerId }),
            loadOverview(rangeParams),
            loadStats(rangeParams),
            loadFrequency({ ...rangeParams, timeGranularity }),
            loadPurchaseCategory({ ...rangeParams, sortType: purchaseSortType }),
            loadBehavior(rangeParams),
            loadInterestSandTop({ ...rangeParams, sortType: interestSortType })
        ])
            .then(
                ([
                    basicResult,
                    overviewResult,
                    statsResult,
                    frequencyResult,
                    purchaseResult,
                    behaviorResult,
                    interestResult
                ]) => {
                    if (disposed) return;
                    setBasic(basicResult);
                    setOverview(overviewResult);
                    setStats(statsResult);
                    setFrequency(frequencyResult.frequencyList || []);
                    setPurchaseCategory(purchaseResult);
                    setBehavior(behaviorResult);
                    setInterestSandTop(interestResult);
                    setErrorMessage("");
                }
            )
            .catch(() => {
                if (!disposed) setErrorMessage("用户画像加载失败");
            });
        return () => {
            disposed = true;
        };
    }, [
        customerId,
        profileRange.beginTime,
        profileRange.endTime,
        timeGranularity,
        purchaseSortType,
        interestSortType,
        loadBasic,
        loadOverview,
        loadStats,
        loadFrequency,
        loadPurchaseCategory,
        loadBehavior,
        loadInterestSandTop
    ]);

    useEffect(() => {
        let disposed = false;
        const params = {
            customerId,
            page: attentionPage,
            size: 10,
            beginTime: profileRange.beginTime,
            endTime: profileRange.endTime,
            ...attentionFilters
        };
        const loadAttention =
            attentionTab === "footprint"
                ? loadFootprint(params)
                : attentionTab === "collected"
                  ? loadCollectedGoods(params)
                  : loadFollowedSupplier(params);

        void loadAttention
            .then(result => {
                if (disposed) return;
                setAttentionTotal(result.total || 0);
                if (attentionTab === "footprint") {
                    setFootprint((result.list || []).map((item, index) => ({ ...item, __index: index + 1 })));
                }
                if (attentionTab === "collected") {
                    setCollectedGoods((result.list || []).map((item, index) => ({ ...item, __index: index + 1 })));
                }
                if (attentionTab === "supplier") {
                    setFollowedSupplier((result.list || []).map((item, index) => ({ ...item, __index: index + 1 })));
                }
            })
            .catch(() => {
                if (!disposed) setErrorMessage("用户画像加载失败");
            });

        return () => {
            disposed = true;
        };
    }, [
        customerId,
        attentionPage,
        attentionTab,
        attentionFilters,
        profileRange.beginTime,
        profileRange.endTime,
        loadFootprint,
        loadCollectedGoods,
        loadFollowedSupplier
    ]);

    useEffect(() => {
        let disposed = false;
        const params = { customerId, page: behaviorPage, size: 10, ...behaviorFilters };
        // 旧 Vue 会用当前行为筛选条件重新统计总计，避免列表与统计卡片口径不一致。
        const totalParams = { customerId, ...behaviorFilters };
        void Promise.all([loadBehaviorTotals(totalParams), loadBehaviorLogs(params)])
            .then(([totalResult, pageResult]) => {
                if (disposed) return;
                setBehaviorTotals(totalResult);
                setBehaviorTotal(pageResult.total || 0);
                setBehaviorLogs(pageResult.list || []);
            })
            .catch(() => {
                if (!disposed) setErrorMessage("用户画像加载失败");
            });
        return () => {
            disposed = true;
        };
    }, [customerId, behaviorPage, behaviorFilters, loadBehaviorLogs, loadBehaviorTotals]);

    const switchAttentionTab = (tab: "footprint" | "collected" | "supplier") => {
        setAttentionTab(tab);
        setAttentionPage(1);
    };

    /** 旧 Vue ProTable 的关注情况筛选先保留草稿，点击搜索后再刷新当前 Tab。 */
    const updateAttentionFilter = (
        key: keyof CustomerProfileAttentionFilters,
        value: string | Array<string | number>
    ) => {
        setAttentionDraftFilters(current => ({ ...current, [key]: value }));
    };

    const applyAttentionFilters = () => {
        const nextFilters = Object.fromEntries(
            Object.entries(attentionDraftFilters).filter(([, value]) =>
                Array.isArray(value) ? value.length > 0 : value != null && value !== ""
            )
        ) as CustomerProfileAttentionFilters;
        setAttentionFilters(nextFilters);
        setAttentionPage(1);
    };

    const resetAttentionFilters = () => {
        setAttentionDraftFilters({});
        setAttentionFilters({});
        setAttentionPage(1);
    };

    /**
     * 打开或搜索品类下拉时调用旧 Vue 的远程接口，并丢弃晚于新请求返回的旧响应。
     * 商品足迹和收藏商品都只查询寄售商品品类（productType=1）。
     */
    const searchProductTypes = async (keyword?: string) => {
        setProductTypeKeyword(keyword?.trim() || "");
        const requestId = ++productTypeRequestRef.current;
        setProductTypeLoading(true);
        try {
            const rows = await loadProductTypes({ keyword: keyword || undefined, productType: 1 });
            if (requestId !== productTypeRequestRef.current) return;
            setProductTypeOptions(
                rows.map(row => ({
                    label: row.areaAbbreviation ? `${row.name} | ${row.areaAbbreviation}` : row.name,
                    value: String(row.id)
                }))
            );
        } catch {
            if (requestId === productTypeRequestRef.current) setProductTypeOptions([]);
        } finally {
            if (requestId === productTypeRequestRef.current) setProductTypeLoading(false);
        }
    };

    const formatStayDuration = (value?: number) => {
        if (value == null || value === 0) return DEFAULT_VALUE;
        if (value < 1) return "<1s";
        return `${Math.floor(value / 60)}min${Math.floor(value % 60)}s`;
    };

    /** 旧 Vue 行为埋点统计卡把 0 秒展示为“<1秒”，与普通列表空值保持区分。 */
    const formatBehaviorTotalStayDuration = (value?: number) => {
        if (value == null) return DEFAULT_VALUE;
        if (value < 1) {
            return [
                <span className="customer-profile-behavior-metric__unit" key="less-than">
                    &lt;
                </span>,
                <span key="one">1</span>,
                <span className="customer-profile-behavior-metric__unit" key="second">
                    秒
                </span>
            ];
        }
        return formartTimeWithUnitAndChinese(value).impreciseTokens.flatMap((token, index) => [
            <span key={`${token.unit}-${index}`}>{token.value}</span>,
            <span className="customer-profile-behavior-metric__unit" key={`${token.unit}-unit-${index}`}>
                {token.label}
            </span>
        ]);
    };

    /** 旧 Vue DataCard 对访问次数使用千分位展示，避免宽屏下数字字形和长度与旧版不一致。 */
    const formatBehaviorVisitCount = (value?: number) => {
        const normalized = Number(value ?? 0);
        return Number.isFinite(normalized) ? normalized.toLocaleString("en-US") : "0";
    };

    /** 旧 Vue 切换时间粒度时，日/月查询不能使用超过一年的起始日期。 */
    const updateTimeGranularity = (nextValue: number) => {
        setTimeGranularity(nextValue);
        if (nextValue !== 4 && dayjs(profileRange.beginTime).isBefore(dayjs().subtract(365, "day"))) {
            const nextRange = getDefaultProfileTimeRange();
            setProfileRange(nextRange);
            setRangeDraft(nextRange);
        }
    };

    const updateBehaviorFilter = (key: keyof CustomerProfileBehaviorFilters, value: string) => {
        setBehaviorDraftFilters(current => ({ ...current, [key]: value }));
    };

    /** 将 Vue 行为埋点 daterange 的两个值转换为统一查询条件。 */
    const updateBehaviorTimeRange = (_value: unknown, formattedValue: [string, string] | null) => {
        setBehaviorDraftFilters(current => ({
            ...current,
            beginTime: formattedValue?.[0] || "",
            endTime: formattedValue?.[1] || ""
        }));
    };

    const applyBehaviorFilters = () => {
        const nextFilters = normalizeCustomerProfileBehaviorFilters(behaviorDraftFilters);
        setBehaviorFilters(nextFilters);
        setBehaviorPage(1);
    };

    const exportBehaviorLogs = async () => {
        if (behaviorTotal > 10000) {
            setErrorMessage("导出失败，导出内容超过10000条，请筛选后重新导出");
            return;
        }
        setBehaviorExporting(true);
        try {
            // Vue 导出将 ProTable 当前页码和条数一并传给导出接口，保持后端查询契约。
            await loadBehaviorExport({ customerId, page: behaviorPage, size: 10, ...behaviorFilters });
        } catch {
            setErrorMessage("导出失败");
        } finally {
            setBehaviorExporting(false);
        }
    };

    // 画像分析区沿用 Vue 的图形化呈现，避免把趋势图和雷达图降级成普通表格。
    const frequencyChartOption = useMemo(() => buildFrequencyChartOption(frequency), [frequency]);
    const behaviorChartOption = useMemo(() => buildBehaviorChartOption(behavior), [behavior]);
    const purchaseCategoryColumns: TableColumnProps<CustomerProfilePurchaseCategory & { rank: number }>[] = [
        {
            label: "排名",
            minWidth: 140,
            prop: "rank",
            render: ({ row }) => (
                <span className={`customer-profile-rank customer-profile-rank--${row.rank}`}>{row.rank}</span>
            )
        },
        { label: "品类", minWidth: 250, prop: "productTypeName" },
        { label: "订单量", minWidth: 150, prop: "orderCount" },
        {
            label: "下单吨数(吨)",
            minWidth: 150,
            prop: "orderTon",
            render: ({ row }) => (row.orderTon == null ? "" : row.orderTon === 0 ? "0" : formatNumber(row.orderTon, 2))
        }
    ];
    const interestSandColumns: TableColumnProps<CustomerProfileInterestSand & { rank: number }>[] = [
        {
            label: "排名",
            minWidth: 140,
            prop: "rank",
            render: ({ row }) => (
                <span className={`customer-profile-rank customer-profile-rank--${row.rank}`}>{row.rank}</span>
            )
        },
        { label: "品类", minWidth: 250, prop: "productTypeName" },
        { label: "用户访问次数", minWidth: 150, prop: "browseCount" },
        {
            label: "用户停留时长",
            minWidth: 150,
            prop: "stayTime",
            render: ({ row }) => formatTimeEn(Number(row.stayTime))
        }
    ];
    const supplierColumns: TableColumnProps<CustomerProfileSupplierItem>[] = [
        { label: "序号", minWidth: 85, prop: "__index" },
        { label: "供应商名称", minWidth: 390, prop: "supplierName" },
        {
            label: "供应商类型",
            minWidth: 300,
            prop: "supplierType",
            render: ({ row }) => ENTERPRISE_SUPPLIER_TYPE.getIntroByCode(row.supplierType) || DEFAULT_VALUE
        },
        { label: "所属集团", minWidth: 250, prop: "groupName" },
        { label: "法人姓名", minWidth: 125, prop: "legalPerson" },
        { label: "所在地", minWidth: 300, prop: "address" },
        { label: "关注时间", minWidth: 240, prop: "followTime", render: ({ row }) => formatShortDate(row.followTime) }
    ];
    const goodsColumns: TableColumnProps<CustomerProfileGoodsItem>[] = [
        { label: "序号", minWidth: 85, prop: "__index" },
        { label: "商品编号", minWidth: 200, prop: "productId" },
        {
            label: "主图/标题",
            minWidth: 320,
            prop: "title",
            render: ({ row }) => (
                <Space>
                    {row.mainFile?.path ? (
                        <img alt={row.title || "商品主图"} height={40} src={row.mainFile.path} width={40} />
                    ) : null}
                    <span>{row.title || DEFAULT_VALUE}</span>
                </Space>
            )
        },
        { label: "品类", minWidth: 165, prop: "category" },
        { label: "供应商", minWidth: 250, prop: "supplierName" },
        { label: "商品专员", minWidth: 125, prop: "goodsCommissioner" },
        {
            label: "商品状态",
            minWidth: 155,
            prop: "goodsState",
            render: ({ row }) => CONSIGN_STATE.getIntroByCode(row.goodsState) || DEFAULT_VALUE
        },
        {
            label: "发布状态",
            minWidth: 150,
            prop: "publishState",
            render: ({ row }) => CONSIGN_PUBLISH_STATE.getIntroByCode(row.publishState) || DEFAULT_VALUE
        },
        {
            label: attentionTab === "footprint" ? "浏览时间" : "收藏时间",
            prop: attentionTab === "footprint" ? "browseTime" : "collectTime",
            render: ({ row }) => formatShortDate(attentionTab === "footprint" ? row.browseTime : row.collectTime)
        }
    ];
    const behaviorLogColumns: TableColumnProps<CustomerProfileBehaviorLog>[] = [
        { label: "时间", width: 200, prop: "time", render: ({ row }) => formatBehaviorDate(row.time) },
        {
            label: "终端",
            width: 100,
            prop: "clientType",
            render: ({ row }) => CLIENT_TYPE_USER.getIntroByCode(row.clientType) || DEFAULT_VALUE
        },
        {
            label: "模块",
            width: 120,
            prop: "module",
            render: ({ row }) => MODULE_TYPE.getIntroByCode(row.module) || DEFAULT_VALUE
        },
        {
            label: "事件名称",
            width: 232,
            prop: "eventName",
            render: ({ row }) => (
                <span className="customer-profile-behavior-event">{row.eventName || DEFAULT_VALUE}</span>
            )
        },
        {
            label: "停留时长",
            width: 230,
            prop: "stayDuration",
            render: ({ row }) => formatStayDuration(row.stayDuration)
        },
        {
            label: "事件类型",
            width: 120,
            prop: "type",
            render: ({ row }) => TRACK_TYPE_USER.getIntroByCode(row.type) || DEFAULT_VALUE
        },
        {
            label: "详情内容",
            width: 230,
            prop: "detailContent",
            render: ({ row }) =>
                row.detailContent
                    ? typeof row.detailContent === "string"
                        ? row.detailContent
                        : JSON.stringify(row.detailContent)
                    : DEFAULT_VALUE
        }
    ];
    const overviewMetrics = {
        orderTradeAmount: formatProfileMetric(overview?.orderTradeAmount, "元"),
        settlementAmount: formatProfileMetric(overview?.settlementAmount, "元"),
        refundAmount: formatProfileMetric(overview?.refundAmount, "元"),
        orderTon: formatProfileMetric(overview?.orderTon),
        actualSettlementTon: formatProfileMetric(overview?.actualSettlementTon),
        totalOrders: formatProfileMetric(overview?.totalOrders),
        completedOrders: formatProfileMetric(overview?.completedOrders)
    };

    return (
        <section aria-label="customer-profile-page" className="customer-profile-page flex min-h-full flex-col">
            {errorMessage ? <Alert message={errorMessage} role="alert" type="error" /> : null}
            <Card className="customer-profile-time-range" title="画像时间范围">
                <Space wrap>
                    <Button aria-label="全部" type="primary" onClick={() => void applyAllProfileTimeRange()}>
                        全部
                    </Button>
                    <Space className="customer-profile-time-granularity" size={0}>
                        <Button
                            aria-label="日"
                            className={timeGranularity === 1 ? "is-active" : undefined}
                            type="default"
                            onClick={() => updateTimeGranularity(1)}
                        >
                            日
                        </Button>
                        <Button
                            aria-label="月"
                            className={timeGranularity === 3 ? "is-active" : undefined}
                            type="default"
                            onClick={() => updateTimeGranularity(3)}
                        >
                            月
                        </Button>
                        <Button
                            aria-label="年"
                            className={timeGranularity === 4 ? "is-active" : undefined}
                            type="default"
                            onClick={() => updateTimeGranularity(4)}
                        >
                            年
                        </Button>
                    </Space>
                    <ProfileDateRangePicker
                        value={[rangeDraft.beginTime, rangeDraft.endTime]}
                        onChange={updateProfileTimeRangeDraft}
                        timeGranularity={timeGranularity}
                    />
                    {profileDateRangeLabel ? (
                        <span className="customer-profile-date-range-summary">{profileDateRangeLabel}</span>
                    ) : null}
                    <Button
                        aria-label="应用画像时间范围"
                        className="customer-profile-apply-range"
                        onClick={applyProfileTimeRange}
                    >
                        应用
                    </Button>
                </Space>
            </Card>
            <div className="customer-profile-basic">
                <span className="sr-only">用户基本信息</span>
                <Space align="start">
                    <Avatar shape="square" size={140} src={basic?.avatarFile?.path ?? userDefaultAvatar} />
                    <div className="customer-profile-basic__content">
                        <div className="customer-profile-basic__name">{basic?.userName || DEFAULT_VALUE}</div>
                        <div className="customer-profile-basic__tag-row">
                            <div className="customer-profile-basic__company">
                                <img alt="" src={authenticatedIcon} />
                                <span>{basic?.company || "暂未认证企业"}</span>
                            </div>
                            {basic?.identityTags?.length ? (
                                <>
                                    <span aria-hidden="true" className="customer-profile-basic__tag-divider" />
                                    <Space className="customer-profile-basic__tags" wrap>
                                        {basic.identityTags.map(tag => (
                                            <Tag key={tag}>{tag}</Tag>
                                        ))}
                                    </Space>
                                </>
                            ) : null}
                        </div>
                        <div className="customer-profile-basic__params">
                            <div>
                                <span>年龄</span>
                                <span>{display(basic?.age)}</span>
                            </div>
                            <div>
                                <span>性别</span>
                                <span>
                                    {basic?.gender == null
                                        ? DEFAULT_VALUE
                                        : genderLabels[Number(basic.gender)] || DEFAULT_VALUE}
                                </span>
                            </div>
                            <div>
                                <span>IP</span>
                                <span>{basic?.cityCode || DEFAULT_VALUE}</span>
                            </div>
                            <div>
                                <span>常用收货地</span>
                                <span>
                                    {basic?.commonProvince
                                        ? areaLib.getShortAreaName(basic.commonProvince, basic.commonCity) ||
                                          DEFAULT_VALUE
                                        : DEFAULT_VALUE}
                                </span>
                            </div>
                        </div>
                    </div>
                </Space>
            </div>
            <Card className="customer-profile-overview" title="交易概览">
                <Row gutter={[16, 0]}>
                    <Col xs={24} sm={12} md={6}>
                        <div className="customer-profile-overview__item">
                            <Tooltip title={`${overviewMetrics.orderTradeAmount.precise} 元`}>
                                <Statistic
                                    title={`订单交易金额(${overviewMetrics.orderTradeAmount.baseUnit}元)`}
                                    value={overviewMetrics.orderTradeAmount.imprecise}
                                />
                            </Tooltip>
                        </div>
                    </Col>
                    <Col xs={24} sm={12} md={6}>
                        <div className="customer-profile-overview__item">
                            <Tooltip title={`${overviewMetrics.settlementAmount.precise} 元`}>
                                <Statistic
                                    title={`结算金额(${overviewMetrics.settlementAmount.baseUnit}元)`}
                                    value={overviewMetrics.settlementAmount.imprecise}
                                />
                            </Tooltip>
                            <Tooltip title={`${overviewMetrics.refundAmount.precise} 元`}>
                                <div className="customer-profile-overview__secondary">
                                    <span className="label">退款金额</span>
                                    <span className="count">{overviewMetrics.refundAmount.imprecise}</span>
                                    <span className="unit">{overviewMetrics.refundAmount.baseUnit}元</span>
                                </div>
                            </Tooltip>
                        </div>
                    </Col>
                    <Col xs={24} sm={12} md={6}>
                        <div className="customer-profile-overview__item">
                            <Tooltip title={`${overviewMetrics.orderTon.precise} 吨`}>
                                <Statistic
                                    title={`下单吨数(${overviewMetrics.orderTon.baseUnit}吨)`}
                                    value={overviewMetrics.orderTon.imprecise}
                                />
                            </Tooltip>
                            <div className="customer-profile-overview__secondary">
                                <span className="label">实际结算吨数</span>
                                <span className="count">{overviewMetrics.actualSettlementTon.imprecise}</span>
                                <span className="unit">{overviewMetrics.actualSettlementTon.baseUnit}吨</span>
                            </div>
                        </div>
                    </Col>
                    <Col xs={24} sm={12} md={6}>
                        <div className="customer-profile-overview__item">
                            <Tooltip title={`${overviewMetrics.totalOrders.precise} 笔`}>
                                <Statistic
                                    title={`产生订单(${overviewMetrics.totalOrders.baseUnit}笔)`}
                                    value={overviewMetrics.totalOrders.imprecise}
                                />
                            </Tooltip>
                            <div className="customer-profile-overview__secondary">
                                <span className="label">已完成订单</span>
                                <span className="count">{overviewMetrics.completedOrders.imprecise}</span>
                                <span className="unit">{overviewMetrics.completedOrders.baseUnit}笔</span>
                            </div>
                        </div>
                    </Col>
                </Row>
            </Card>
            <Row className="customer-profile-content-row" gutter={8}>
                <Col className="customer-profile-primary-col" xs={24} xl={12}>
                    <Card className="customer-profile-chart-card" title="交易频率分析">
                        <ProfileChart chartOption={frequencyChartOption} height={306} />
                    </Card>
                </Col>
                <Col className="customer-profile-secondary-col" xs={24} xl={12}>
                    <Card
                        className="customer-profile-ranking-card"
                        title="购买品类TOP5"
                        extra={
                            <div aria-label="购买品类TOP5排序方式" className="customer-profile-sort-selector">
                                <button
                                    className={purchaseSortType === 1 ? "is-active" : undefined}
                                    type="button"
                                    onClick={() => setPurchaseSortType(1)}
                                >
                                    订单优先
                                </button>
                                <button
                                    className={purchaseSortType === 2 ? "is-active" : undefined}
                                    type="button"
                                    onClick={() => setPurchaseSortType(2)}
                                >
                                    下单量优先
                                </button>
                            </div>
                        }
                    >
                        <AdminDetailTable<CustomerProfilePurchaseCategory & { rank: number }>
                            rowKey={row => row.productTypeName || "category"}
                            size="small"
                            pagination={false}
                            data={purchaseCategory.map((item, index) => ({ ...item, rank: index + 1 }))}
                            emptyBodyMinHeight={210}
                            columns={purchaseCategoryColumns}
                        />
                    </Card>
                </Col>
            </Row>
            <Row className="customer-profile-content-row" gutter={8}>
                <Col className="customer-profile-primary-col" xs={24} xl={12}>
                    <Card className="customer-profile-chart-card customer-profile-behavior-card" title="用户行为分布">
                        <div className="customer-profile-behavior-chart">
                            <ProfileChart chartOption={behaviorChartOption} height={306} />
                            <div className="customer-profile-behavior-chart__legend">
                                {behavior.map(item => (
                                    <div key={item.moduleName}>
                                        <span>{item.moduleName || DEFAULT_VALUE}</span>
                                        <span>
                                            {item.stayTime === 0
                                                ? "0"
                                                : formartTimeWithUnitAndChinese(Number(item.stayTime)).precise}
                                        </span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </Card>
                </Col>
                <Col className="customer-profile-secondary-col" xs={24} xl={12}>
                    <Card
                        className="customer-profile-ranking-card"
                        title="用户感兴趣砂石TOP5"
                        extra={
                            <div aria-label="用户感兴趣砂石TOP5排序方式" className="customer-profile-sort-selector">
                                <button
                                    className={interestSortType === 1 ? "is-active" : undefined}
                                    type="button"
                                    onClick={() => setInterestSortType(1)}
                                >
                                    停留时长优先
                                </button>
                                <button
                                    className={interestSortType === 2 ? "is-active" : undefined}
                                    type="button"
                                    onClick={() => setInterestSortType(2)}
                                >
                                    访问次数优先
                                </button>
                            </div>
                        }
                    >
                        <AdminDetailTable<CustomerProfileInterestSand & { rank: number }>
                            rowKey={row => row.productTypeName || "interest"}
                            size="small"
                            pagination={false}
                            data={interestSandTop.map((item, index) => ({ ...item, rank: index + 1 }))}
                            emptyBodyMinHeight={210}
                            columns={interestSandColumns}
                        />
                    </Card>
                </Col>
            </Row>
            <Card
                className="customer-profile-attention-card"
                title={
                    <div className="customer-profile-attention-title">
                        <span>用户关注情况</span>
                        <span className="customer-profile-attention-title__stats">
                            <span>
                                商品足迹 <strong>{stats?.footprintCount ?? 0}</strong>
                            </span>
                            <span>
                                收藏商品 <strong>{stats?.collectCount ?? 0}</strong>
                            </span>
                            <span>
                                关注供应商数 <strong>{stats?.followSupplierCount ?? 0}</strong>
                            </span>
                        </span>
                    </div>
                }
            >
                <Space className="customer-profile-attention-tabs" wrap>
                    <button
                        aria-label="商品足迹"
                        className={attentionTab === "footprint" ? "font-semibold text-blue-600" : ""}
                        type="button"
                        onClick={() => switchAttentionTab("footprint")}
                    >
                        商品足迹
                    </button>
                    <button
                        aria-label="收藏商品"
                        className={attentionTab === "collected" ? "font-semibold text-blue-600" : ""}
                        type="button"
                        onClick={() => switchAttentionTab("collected")}
                    >
                        收藏商品
                    </button>
                    <button
                        aria-label="关注供应商"
                        className={attentionTab === "supplier" ? "font-semibold text-blue-600" : ""}
                        type="button"
                        onClick={() => switchAttentionTab("supplier")}
                    >
                        关注供应商
                    </button>
                </Space>
                <div className="customer-profile-attention-filter">
                    {attentionTab === "supplier" ? (
                        <>
                            <Input
                                aria-label="供应商名称"
                                className="customer-profile-attention-filter__supplier-name"
                                placeholder="请输入供应商名称搜索"
                                value={attentionDraftFilters.supplierName || ""}
                                onChange={event => updateAttentionFilter("supplierName", event.target.value)}
                            />
                            <Select
                                aria-label="供应商类型"
                                className="customer-profile-attention-filter__supplier-type"
                                classNames={{ popup: { root: "customer-profile-supplier-dropdown" } }}
                                maxTagCount={1}
                                mode="multiple"
                                notFoundContent={productTypeKeyword ? "无数据" : null}
                                placeholder="请选择供应商类型"
                                value={attentionDraftFilters.supplierType || []}
                                options={ENTERPRISE_SUPPLIER_TYPE.options}
                                onChange={value => updateAttentionFilter("supplierType", value)}
                            />
                        </>
                    ) : (
                        <>
                            <Input
                                aria-label="商品名称或编号"
                                className="customer-profile-attention-filter__keyword"
                                placeholder="请输入商品名称或商品编号搜索"
                                value={attentionDraftFilters.keyword || ""}
                                onChange={event => updateAttentionFilter("keyword", event.target.value)}
                            />
                            <Select
                                allowClear
                                aria-label="商品品类"
                                className="customer-profile-attention-filter__category"
                                filterOption={false}
                                loading={productTypeLoading}
                                notFoundContent="无数据"
                                placeholder="请选择品类"
                                value={attentionDraftFilters.productTypeId || undefined}
                                options={productTypeOptions}
                                showSearch
                                onChange={value => updateAttentionFilter("productTypeId", value || "")}
                                onSearch={value => void searchProductTypes(value)}
                            />
                            <Input
                                aria-label="供应商名称"
                                className="customer-profile-attention-filter__supplier-name"
                                placeholder="请输入供应商名称搜索"
                                value={attentionDraftFilters.supplierName || ""}
                                onChange={event => updateAttentionFilter("supplierName", event.target.value)}
                            />
                        </>
                    )}
                    <Button type="primary" onClick={applyAttentionFilters}>
                        <TableSearchIcon name="search" />
                        搜索
                    </Button>
                    <Button
                        aria-label="重置关注情况筛选"
                        className="customer-profile-attention-filter__reset"
                        icon={<TableSearchIcon name="reset" />}
                        onClick={resetAttentionFilters}
                    />
                </div>
                {attentionTab === "supplier" ? (
                    <>
                        <AdminDetailTable<CustomerProfileSupplierItem>
                            rowKey={row => String(row.supplierId || row.supplierName || "")}
                            data={followedSupplier}
                            emptyBodyMinHeight={150}
                            pagination={false}
                            size="small"
                            columns={supplierColumns}
                            append={
                                attentionTotal > 0 ? (
                                    <div className="admin-table-composed-pagination">
                                        <ZhPagination
                                            current={attentionPage}
                                            locale={{ jump_to: "前往" }}
                                            pageSize={10}
                                            total={attentionTotal}
                                            onChange={setAttentionPage}
                                        />
                                    </div>
                                ) : null
                            }
                        />
                    </>
                ) : (
                    <>
                        <AdminDetailTable<CustomerProfileGoodsItem>
                            rowKey={row => String(row.productId || row.title || "")}
                            data={attentionTab === "footprint" ? footprint : collectedGoods}
                            emptyBodyMinHeight={150}
                            pagination={false}
                            size="small"
                            columns={goodsColumns}
                            append={
                                attentionTotal > 0 ? (
                                    <div className="admin-table-composed-pagination">
                                        <ZhPagination
                                            current={attentionPage}
                                            locale={{ jump_to: "前往" }}
                                            pageSize={10}
                                            total={attentionTotal}
                                            onChange={setAttentionPage}
                                        />
                                    </div>
                                ) : null
                            }
                        />
                    </>
                )}
            </Card>
            <Card className="customer-profile-behavior-track-card" title="行为埋点">
                <AdminDetailTable<CustomerProfileBehaviorLog>
                    rowKey={row => `${row.time || "log"}-${row.eventName || "event"}`}
                    size="small"
                    data={behaviorLogs}
                    emptyBodyMinHeight={122}
                    pagination={false}
                    columns={behaviorLogColumns}
                    isShowResetBtn={false}
                    isShowSearchBtn={false}
                    tableHeader={
                        <div className="admin-table-composed-header">
                            <div className="customer-profile-behavior-metrics">
                                <div className="customer-profile-behavior-metric">
                                    <span className="customer-profile-behavior-metric__title">用户停留时长</span>
                                    <span className="customer-profile-behavior-metric__value">
                                        {formatBehaviorTotalStayDuration(behaviorTotals?.totalStayDurationMs)}
                                    </span>
                                </div>
                                <div className="customer-profile-behavior-metric">
                                    <span className="customer-profile-behavior-metric__title">用户访问次数</span>
                                    <span className="customer-profile-behavior-metric__value">
                                        {formatBehaviorVisitCount(behaviorTotals?.totalVisitCount)}
                                    </span>
                                </div>
                            </div>

                            <Space className="customer-profile-behavior-filter" wrap>
                                <Permission anyOf={["ROLE_SS_CUSTOMER_W"]}>
                                    <Button
                                        aria-label="导出行为埋点"
                                        loading={behaviorExporting}
                                        type="primary"
                                        onClick={() => void exportBehaviorLogs()}
                                    >
                                        导出
                                    </Button>
                                </Permission>
                                <Input
                                    aria-label="事件名称"
                                    placeholder="请输入事件名称"
                                    value={behaviorDraftFilters.keyword}
                                    onChange={event => updateBehaviorFilter("keyword", event.target.value)}
                                />
                                <Select
                                    allowClear
                                    aria-label="模块"
                                    className="customer-profile-behavior-filter__module"
                                    placeholder="请选择模块"
                                    options={MODULE_TYPE.options}
                                    value={behaviorDraftFilters.functionType || undefined}
                                    onChange={value => updateBehaviorFilter("functionType", String(value ?? ""))}
                                />
                                <Select
                                    allowClear
                                    aria-label="终端"
                                    className="customer-profile-behavior-filter__client"
                                    placeholder="请选择终端"
                                    options={CLIENT_TYPE_USER.options}
                                    value={
                                        behaviorDraftFilters.clientType
                                            ? Number(behaviorDraftFilters.clientType)
                                            : undefined
                                    }
                                    onChange={value => updateBehaviorFilter("clientType", String(value ?? ""))}
                                />
                                <Select
                                    allowClear
                                    aria-label="事件类型"
                                    className="customer-profile-behavior-filter__type"
                                    placeholder="请选择事件类型"
                                    options={TRACK_TYPE_USER.options}
                                    value={behaviorDraftFilters.type ? Number(behaviorDraftFilters.type) : undefined}
                                    onChange={value => updateBehaviorFilter("type", String(value ?? ""))}
                                />
                                <ZhDateRangePicker
                                    className="customer-profile-behavior-filter__date"
                                    clearable
                                    endPlaceholder="结束时间"
                                    format="YYYY-MM-DD"
                                    rangeSeparator="-"
                                    startPlaceholder="开始时间"
                                    type="daterange"
                                    value={[behaviorDraftFilters.beginTime || "", behaviorDraftFilters.endTime || ""]}
                                    valueFormat="YYYY-MM-DD"
                                    onChange={updateBehaviorTimeRange}
                                />
                                <Button
                                    aria-label="查询行为埋点"
                                    icon={<TableSearchIcon name="search" />}
                                    type="primary"
                                    onClick={applyBehaviorFilters}
                                >
                                    搜索
                                </Button>
                                <Button
                                    aria-label="重置行为埋点筛选"
                                    className="customer-profile-behavior-filter__reset"
                                    icon={<TableSearchIcon name="reset" />}
                                    onClick={() => {
                                        setBehaviorDraftFilters({});
                                        setBehaviorFilters({});
                                        setBehaviorPage(1);
                                    }}
                                />
                            </Space>
                        </div>
                    }
                    append={
                        behaviorTotal > 0 ? (
                            <div className="admin-table-composed-pagination">
                                <ZhPagination
                                    current={behaviorPage}
                                    locale={{ jump_to: "前往" }}
                                    pageSize={10}
                                    total={behaviorTotal}
                                    onChange={setBehaviorPage}
                                />
                            </div>
                        ) : null
                    }
                />
            </Card>
            <div className="customer-profile-operation-btn">
                <Button aria-label="返回" onClick={onBack}>
                    返回
                </Button>
            </div>
        </section>
    );
}

export default CustomerProfilePage;
